import type { StatutoryMatrixHead } from './types';

/**
 * Totals for one Zoho ledger, comparing like with like.
 *
 * WHY THE API'S OWN TOTALS ARE NOT USED HERE
 * The API sums every month on each side independently. That is wrong the moment a month has one
 * side and not the other, and "Employer PF contribution" is exactly that case: September has no
 * challan on file, but Zoho still booked 1,00,305 for it. Summing both columns whole put the
 * September figure in the Zoho total with nothing opposite it, and reported the year's gap as
 * −2,91,119 when the comparable gap is −3,91,424. A total that quietly compares eleven months of
 * documents against twelve months of books flatters the result by a lakh.
 *
 * THE RULE: a month counts when we hold a DOCUMENT for it — a challan, or a return where no
 * challan exists. Then:
 *
 *   - document present, books present  -> compare, difference counts
 *   - document present, books empty    -> counts, and counts as a full shortfall. "Not booked" is
 *                                        the finding, not a reason to look away.
 *   - document absent                  -> EXCLUDED from the totals, reported separately. We cannot
 *                                        say whether the books are right about a month we have no
 *                                        paperwork for, and pretending otherwise is how a missing
 *                                        document turns into a false clean bill.
 */
export interface HeadTotals {
	/** months with a document, so both sides are answerable */
	comparableMonths: number;
	documentRs: number;
	zohoRs: number;
	/** zohoRs − documentRs across comparable months only */
	netRs: number;
	/** months where a document exists and nothing at all was booked */
	notBookedMonths: string[];
	notBookedRs: number;
	/** months with no challan or return — left out of the totals above */
	excludedMonths: string[];
	/** what Zoho booked in those excluded months, shown so it is not silently dropped */
	excludedZohoRs: number;
}

export function headTotals(head: StatutoryMatrixHead): HeadTotals {
	const t: HeadTotals = {
		comparableMonths: 0,
		documentRs: 0,
		zohoRs: 0,
		netRs: 0,
		notBookedMonths: [],
		notBookedRs: 0,
		excludedMonths: [],
		excludedZohoRs: 0,
	};

	for (const r of head.rows) {
		const doc = r.challan_rs ?? r.return_rs;
		const zoho = r.zoho_rs;

		if (doc === null || doc === undefined) {
			// Nothing to measure against. A month with neither side is simply empty and not worth
			// reporting; a month where Zoho booked something is worth saying out loud.
			if (zoho) {
				t.excludedMonths.push(r.wage_month);
				t.excludedZohoRs += zoho;
			}
			continue;
		}

		t.comparableMonths += 1;
		t.documentRs += doc;
		t.zohoRs += zoho ?? 0;
		if (!zoho) {
			t.notBookedMonths.push(r.wage_month);
			t.notBookedRs += doc;
		}
	}

	t.netRs = t.zohoRs - t.documentRs;
	return t;
}
